import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled'
import SyncIcon from '@mui/icons-material/Sync'
import {
  Box,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { FC, useCallback, useMemo, useState } from 'react'
import { isZakenOrganization, useOrganizationSettings } from '../../utils/useOrganizationSettings'
import {
  Datagrid,
  DateField,
  FunctionField,
  ListContextProvider,
  Pagination,
  TextField,
  useGetList,
  useList,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate
} from 'react-admin'
import AddIconButton from '../../components/buttons/AddIconButton'
import CreateDialog from '../../components/CreateDialog'
import { getUrl, httpClient } from '../../providers/data'
import { Samenwerking, ZaakDocument } from '../../types/apiRequests'
import CreateDocumentForm from './Forms/CreateDocumentForm'

const DocumentActionsMenu: FC<{
  doc: ZaakDocument
  samenwerkingId: string
  isClosed?: boolean
  isZaken?: boolean
}> = ({ doc, samenwerkingId, isClosed, isZaken }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }, [])

  const handleClose = useCallback(() => setAnchorEl(null), [])

  const handleDownload = useCallback(() => {
    handleClose()
    const fallback = doc.name ? `?bestandsNaam=${encodeURIComponent(doc.name)}` : ''
    const downloadUrl = `${getUrl()}/documenten/${encodeURIComponent(doc.id)}/download${fallback}`
    httpClient(downloadUrl, { method: 'GET' })
      .then(({ json }: { json: { data: string; mimeType: string; filename: string; id: string } }) => {
        const byteChars = atob(json.data)
        const bytes = Uint8Array.from(byteChars, c => c.charCodeAt(0))
        const blob = new Blob([bytes], { type: json.mimeType || 'application/octet-stream' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = json.filename || `${json.id}`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
      })
      .catch(() => notify('ra.notification.http_error', { type: 'warning' }))
  }, [doc, handleClose, notify])

  const handleSync = useCallback(() => {
    handleClose()
    const encodedSamenwerkingId = encodeURIComponent(samenwerkingId)
    const encodedDocId = encodeURIComponent(doc.id)
    const encodedBron = encodeURIComponent(doc.bron)
    httpClient(
      `${getUrl()}/documenten/${encodedSamenwerkingId}/sync/${encodedDocId}?bron=${encodedBron}`,
      { method: 'POST' }
    )
      .then(() => {
        notify('show.synced', { type: 'info' })
        refresh()
      })
      .catch(() => notify('ra.notification.http_error', { type: 'warning' }))
  }, [doc, samenwerkingId, handleClose, notify, refresh])

  const handleDelete = useCallback((documentId: string) => {
    handleClose()
    const params = samenwerkingId ? `?samenwerkingId=${encodeURIComponent(samenwerkingId)}` : ''
    httpClient(`${getUrl()}/documenten/${encodeURIComponent(documentId)}${params}`, { method: 'DELETE' })
      .then(() => {
        // eslint-disable-next-line camelcase
        notify('ra.notification.deleted', { type: 'info', messageArgs: { smart_count: 1 } })
        refresh()
      })
      .catch(() => notify('ra.notification.http_error', { type: 'warning' }))
  }, [samenwerkingId, handleClose, notify, refresh])

  const syncTarget = doc.bron === 'zaken' ?
    translate('data.sourceDso') : translate('data.sourceZaken')

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={handleDownload}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{translate('actions.download')}</ListItemText>
        </MenuItem>
        {isZaken && !isClosed && (
          <MenuItem onClick={handleSync} disabled={doc.synced}>
            <ListItemIcon><SyncIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{translate('actions.syncTo', { target: syncTarget })}</ListItemText>
          </MenuItem>
        )}
        {!isClosed && doc.bron === 'dso' && (
          <MenuItem onClick={() => handleDelete(doc.id)}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>{translate('ra.action.delete')}</ListItemText>
          </MenuItem>
        )}
        {!isClosed && doc.bron === 'zaken' && doc.synced && doc.dso_document_id && (
          <MenuItem onClick={() => handleDelete(doc.dso_document_id!)}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>{translate('actions.deleteFromDso')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  )
}

const CollaborationsDocuments: FC = () => {
  const translate = useTranslate()
  const record = useRecordContext<Samenwerking>()
  const isClosed = record?.status === 'GESLOTEN'
  const [search, setSearch] = useState('')
  const orgSettings = useOrganizationSettings()
  const isZaken = isZakenOrganization(orgSettings)

  const { data, isLoading, error } = useGetList<ZaakDocument>(
    `documenten/${record?.id}`,
    {
      filter: {},
      pagination: { page: 1, perPage: 1000 },
    }
  )

  const filtered = useMemo(() => {
    if (!data) return []
    if (!search.trim()) return data
    const q = search.toLowerCase()
    return data.filter(entry =>
      entry.name?.toLowerCase().includes(q)
      || entry.created_by?.toLowerCase().includes(q)
    )
  }, [data, search])

  const listContext = useList<ZaakDocument>({ data: filtered, isLoading, perPage: 10 })

  if (isLoading) return <div />
  if (error) {
    return (
      <Typography color="error">
        {translate('ra.notification.http_error')}
      </Typography>
    )
  }

  return (
    <Grid container spacing={3}>
      <Grid item md={12}>
        <Box
          sx={{
            border: 1,
            borderColor: 'grey.300',
            borderRadius: 1,
            p: 2,
            height: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <InsertDriveFileIcon sx={{ mr: '10px' }} fontSize="small" />
              {translate('show.documents')}
              {!isClosed && (
                <CreateDialog
                  resource={`documenten/${record?.samenwerkingId}`}
                  defaultValues={{
                    samenwerkingId: record?.id,
                    nummerBinnenSysteem: record?.nummerBinnenSysteem,
                    kenmerkSysteem: record?.kenmerkSysteem,
                  }}
                  dialogTitle="data.addDocument"
                  dialogSubtitle={record?.titel}
                  dialogIcon={<InsertDriveFileIcon />}
                  renderTrigger={open => <AddIconButton onClick={open} />}
                >
                  <CreateDocumentForm />
                </CreateDialog>
              )}
            </Typography>
            <MuiTextField
              size="small"
              placeholder={translate('ra.action.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
          </Box>
          <ListContextProvider value={listContext}>
            <Datagrid bulkActionButtons={false} rowClick={false}>
              <FunctionField
                label="data.name"
                render={(doc: ZaakDocument) => (
                  <Typography variant="body2" sx={{ wordBreak: 'break-word', minWidth: 150 }}>
                    {doc.name}
                  </Typography>
                )}
              />
              <DateField
                label="data.createdAt"
                source="last_modified"
                showTime
              />
              <TextField
                label="data.by"
                source="created_by"
              />
              <FunctionField
                label="data.source"
                render={(doc: ZaakDocument) => (
                  <Chip
                    size="small"
                    label={doc.bron === 'zaken' ? translate('data.sourceZaken') : translate('data.sourceDso')}
                    color={doc.bron === 'zaken' ? 'primary' : 'default'}
                    variant="outlined"
                    sx={{ whiteSpace: 'nowrap' }}
                  />
                )}
              />
              <FunctionField
                label="data.synced"
                render={(doc: ZaakDocument) => (
                  <Tooltip
                    title={doc.synced ? translate('show.synced') : translate('show.notSynced')}>
                    {doc.synced
                      ? <CheckCircleIcon color="success" fontSize="small" />
                      : <SyncDisabledIcon color="disabled" fontSize="small" />}
                  </Tooltip>
                )}
              />
              <FunctionField
                label=""
                render={(doc: ZaakDocument) => (
                  <DocumentActionsMenu
                    doc={doc}
                    samenwerkingId={record?.id ?? ''}
                    isClosed={isClosed}
                    isZaken={isZaken}
                  />
                )}
              />
            </Datagrid>
            <Pagination />
          </ListContextProvider>
        </Box>
      </Grid>
    </Grid>
  )
}

export default CollaborationsDocuments
